import React, { useMemo } from 'react'
import Modal from 'components/Modal'
import { ButtonContainer } from 'styles/shared'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import { color } from 'styles/colors'
import styled from 'styled-components'

import { useSessionContext } from 'hooks/SessionContext'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

const FREE_TRIAL_TECH_FEATURES = [
  'Unlimited Access to Tech Assistant AI Chatbot in Technician App',
  'Extended Runtime History',
  'Maintenance Trends'
]

const FREE_TRIAL_DEALER_FEATURES = [
  ...FREE_TRIAL_TECH_FEATURES,
  'System Report Emailed to Customers',
  'Historical System Report Storage'
]

const getWholeDayDiff = (start: Date, end: Date) =>
  Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))

const getDaysRemaining = (endDateValue: string | null): number | null => {
  if (!endDateValue) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = new Date(endDateValue)
  if (isNaN(end.getTime())) return null
  return getWholeDayDiff(today, end)
}

type PremiumDetailsProps = {
  closeCallback: () => void
  isVisible?: boolean
}

const PremiumDetailsModal = ({ closeCallback, isVisible = false }: PremiumDetailsProps) => {
  const { session: { isTechnician, isDealer } } = useSessionContext()
  const { premiumFreeTrial, premiumFreeTrialEndDate } = usePremiumFeaturesContext()

  const featureDetails = useMemo(
    () => (isDealer ? FREE_TRIAL_DEALER_FEATURES : isTechnician ? FREE_TRIAL_TECH_FEATURES : []),
    [isDealer, isTechnician]
  )

  const daysRemaining = useMemo(
    () => (premiumFreeTrial ? getDaysRemaining(premiumFreeTrialEndDate) : null),
    [premiumFreeTrial, premiumFreeTrialEndDate]
  )
  const daysRemainingText = `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`

  const modalTitle = `Diagnostics Premium${premiumFreeTrial ? ' Free Trial' : ''}`

  return (
    <Modal
      variant="sm"
      isDisplayed={isVisible}
      closeCallback={() => closeCallback()}
      paperStyles={{ maxWidth: 550 }}
    >
      <S.Container>
        <S.Title>{modalTitle}</S.Title>

        {premiumFreeTrial && (
          <S.DaysRemainingText>
            <S.RedText>{daysRemainingText}</S.RedText> in your Diagnostics Premium free 30 day trial.
          </S.DaysRemainingText>
        )}

        {!!featureDetails.length && (
          <S.DetailsListContainer>
            <span>This includes:</span>
            <S.DetailsList>
              {featureDetails.map((feature) => (
                <S.DetailsListItem key={feature}>{feature}</S.DetailsListItem>
              ))}
            </S.DetailsList>
          </S.DetailsListContainer>
        )}

        <S.ItalicText>Plus everything included in the Standard plan</S.ItalicText>
      </S.Container>

      <S.ButtonContainer noBorder>
        <Button decoration="positive" text="OK" onPress={closeCallback} styles={buttonStyle} />
      </S.ButtonContainer>
    </Modal>
  )
}

export default PremiumDetailsModal

const buttonStyle: ButtonStyles = {
  button: {
    minWidth: 80
  },
  text: {
    fontSize: 16
  }
} as ButtonStyles

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    font-size: 14px;
  `,
  Title: styled.span`
    color: ${color('premiumModalTitleText')};
    align-self: center;
    font-size: 28px;
    font-weight: 600;
    margin-top: 10px;
    margin-bottom: 15px;
  `,
  DaysRemainingText: styled.span`
    font-size: 16px;
  `,
  RedText: styled.span`
    color: ${color('premiumModalDaysRemaining')}
  `,
  DetailsListContainer: styled.div`
    margin-top: 10px;
    margin-bottom: 10px;
    line-height: 32px;
  `,
  DetailsList: styled.ul`
    padding-left: 0;
    margin-top: 0;
    margin-bottom: 0;
  `,
  DetailsListItem: styled.li`
    margin-left: 25px;
    line-height: 21px;
  `,
  ItalicText: styled.span`
    font-style: italic;
  `,
  ButtonContainer: styled(ButtonContainer)`
    margin-top: 0px;
  `
}
